import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Drawer,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useState } from 'react';
import {
  adminEventGet,
  eventEditOptionsGet,
  eventEdit,
  eventDelete,
} from 'api/api';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { FastField, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import MuiTextField from '@mui/material/TextField';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  CheckboxWithLabel,
  Select,
  TextField,
} from 'formik-mui';
import React from 'react';
import { DateTimePicker } from 'formik-mui-lab';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';
import { useMedia } from 'react-use';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  TimeScale,
  ArcElement,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import Annotation from 'chartjs-plugin-annotation';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { CircularProgressWithLabel } from 'components/CircularProgressWithLabel';

const formatNumber = (number: number) => {
  return new Intl.NumberFormat().format(number);
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Annotation,
);

export const AdminEvent = ({
  selectedOrganization,
}: {
  selectedOrganization: any;
}) => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const hasPathParam = !!eventId;
  const [dialogOpen, setDialogOpen] = useState(false);

  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [selected, setSelected] = useState<any | null>(null);
  const theme = useTheme();
  const isWide = useMedia('(min-width: 500px)');

  const queryClient = useQueryClient();

  const pieColors = [
    theme.palette.primary.main,
    '#666',
    '#ccc',
    '#444',
    '#999',
    '#000',
    '#fff',
  ];

  const pieOptions: ChartOptions<any> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '83%',
    color: '#fff',
    fullSize: false,
    layout: {
      autoPadding: false,
    },
    plugins: {
      legend: {
        display: false,
        position: 'right' as const,
        align: 'center',
        labels: {
          textAlign: 'left',
          color: theme.palette.text.secondary,
          boxWidth: 8,
          boxHeight: 8,
          usePointStyle: true,
        },
      },
      title: {
        display: false,
        text: 'Types',
        color: theme.palette.text.secondary,
        font: { weight: '500', size: 15 },
      },
    },
  };

  const alert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const {
    data: eventOptionsResponse,
    isLoading: eventOptionsIsLoading,
    error: eventOptionsError,
    refetch: refetchEventOptions,
  } = useQuery(
    ['eventOptionsGet', selectedOrganization?.id],
    eventEditOptionsGet(selectedOrganization?.id as number),
    {
      enabled: !!rightDrawerOpen,
      refetchOnWindowFocus: false,
    },
  );
  const eventOptions: any = eventOptionsResponse?.data;

  console.log('selectedOrganization', selectedOrganization);

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery(['adminEventGet', eventId], adminEventGet(Number(eventId!)), {
    enabled: hasPathParam,
    refetchOnWindowFocus: false,
  });
  const event: any = response?.data;

  const dateFormat = 'YYYY-MM-DD HH:mm';

  const {
    mutateAsync,
    error: mutateError,
    data: mutateData,
    isLoading: mutateIsLoading,
  } = useMutation('eventEdit', eventEdit, {
    onError: (err: any) => {
      alert('Error', err.message);
    },
    onSettled: () => {
      refetch();
      queryClient.invalidateQueries(['adminEventGet', 'eventsGet', 'events']);
    },
  });

  const formatDuration = (minutes) => {
    if (Math.floor(minutes / 60)) {
      return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    } else {
      return `${minutes}m`;
    }
  };

  if (!event) {
    return (
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const data = {
    datasets: [
      {
        label: 'Entered',
        data: event.tickets.map((ticket, index) => ({
          x: ticket.usedAt,
          y: index,
        })),
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main,
      },
    ],
  };
  console.log(
    moment().add(1, 'w').isoWeekday('Saturday').startOf('day').add(20, 'h'),
  );

  const tempResult = {};

  for (const { ticketType } of event.tickets) {
    tempResult[ticketType.name] = {
      id: ticketType.id,
      name: ticketType.name,
      count: tempResult[ticketType.name]
        ? tempResult[ticketType.name].count + 1
        : 1,
    };
  }
  const ticketTypesSold = Object.values(tempResult);

  const myAnnotations = [event.preShowStartTime, event.featureStartTime].filter(
    Boolean,
  );

  return (
    <>
      <React.Fragment key="right">
        <Drawer
          anchor="right"
          open={rightDrawerOpen}
          onClose={() => {
            refetch();
            setRightDrawerOpen(false);
          }}
        >
          <Box sx={{ width: { xs: '100vw', sm: '600px' } }}>
            <Typography
              variant="h1"
              sx={{ fontSize: '1.3em', fontWeight: 500, m: '16px 24px' }}
            >
              {!!selected ? 'Edit event' : 'Add event'}
            </Typography>
            <Divider />
            <Box sx={{ padding: '24px' }}>
              <Box sx={{ mb: '20px' }}>
                <Typography sx={{ fontWeight: 500, display: 'inline' }}>
                  Organization:{' '}
                </Typography>
                {selectedOrganization?.name}
              </Box>
              {/*
              {!!selected && (
                <Box sx={{ mb: '16px' }}>
                  <Typography sx={{ fontWeight: 500, display: 'inline' }}>
                    User:{' '}
                  </Typography>
                  {selected?.name}
                </Box>
              )}
              */}
              {(!!eventOptionsIsLoading || !!isLoading) && (
                <Box sx={{ textAlign: 'center', mt: 6 }}>
                  <CircularProgress />
                </Box>
              )}
              {!!eventOptions && (
                <>
                  {!selected &&
                    !eventOptions?.eventTypes &&
                    'You need to add an event type.'}
                  {!selected &&
                    !eventOptions?.venueParts &&
                    'You need to add a venue and its part(s).'}
                  {!selected &&
                    !eventOptions?.ticketTypeSets &&
                    'You need to add a ticket type set and make sure it has at least one ticket in it.'}

                  <Formik
                    validateOnMount
                    initialValues={{
                      name: selected?.name || '',
                      venuePartId: selected?.venuePartId || '',
                      eventTypeId: selected?.eventTypeId || '',
                      ticketTypeSetId: selected?.ticketTypeSetId || '',
                      public: selected?.public || true,
                      preShowStartTime:
                        selected?.preShowStartTime ||
                        moment()
                          .add(1, 'w')
                          .isoWeekday('Saturday')
                          .startOf('day')
                          .add(20, 'h'),
                      featureStartTime:
                        selected?.featureStartTime ||
                        moment()
                          .add(1, 'w')
                          .isoWeekday('Saturday')
                          .startOf('day')
                          .add(20, 'h'),
                      featureEndTime:
                        selected?.featureEndTime ||
                        moment()
                          .add(1, 'w')
                          .isoWeekday('Saturday')
                          .startOf('day')
                          .add(22, 'h'),
                      salesCutOffTime:
                        selected?.salesCutOffTime ||
                        moment()
                          .add(1, 'w')
                          .isoWeekday('Saturday')
                          .startOf('day')
                          .add(22, 'h'),
                    }}
                    validationSchema={Yup.object().shape({
                      name: Yup.string().required('Required'),
                    })}
                    onSubmit={async (values: any, { setSubmitting }) => {
                      values.organizationId = selectedOrganization.id;
                      try {
                        values.id = event.id;
                        await mutateAsync({ values });
                      } catch (error: any) {
                        alert(error.error, error.message);
                      }
                      refetch();
                      setSubmitting(false);
                      setRightDrawerOpen(false);
                    }}
                  >
                    {({
                      submitForm,
                      isSubmitting,
                      values,
                      isValid,
                      handleChange,
                      errors,
                      touched,
                      handleBlur,
                      setFieldValue,
                    }) => (
                      <Form style={{ width: '100%' }}>
                        <Box
                          sx={{
                            width: '100%',
                            marginInline: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            position: 'relative',
                          }}
                        >
                          <Box className="small-formik-select-fix">
                            <Field
                              component={Autocomplete}
                              options={
                                eventOptions?.eventTypes.map((t) => t.id) || []
                              }
                              getOptionLabel={(selectedEventTypeId: any) =>
                                eventOptions?.eventTypes.find(
                                  (t) => t.id === selectedEventTypeId,
                                )?.name || ''
                              }
                              onChange={(_e, newValue) => {
                                setFieldValue('eventTypeId', newValue);
                                const selectedEventType =
                                  eventOptions?.eventTypes.find(
                                    (t) => t.id === newValue,
                                  );

                                console.log(
                                  'selectedEventType',
                                  selectedEventType,
                                );

                                if (selectedEventType) {
                                  if (!values.name) {
                                    setFieldValue(
                                      'name',
                                      selectedEventType.name,
                                    );
                                  }

                                  const currentStartTime =
                                    values.featureStartTime;

                                  if (
                                    !selected.featureEndTime &&
                                    currentStartTime
                                  ) {
                                    setFieldValue(
                                      'featureEndTime',
                                      moment(currentStartTime).add(
                                        selectedEventType.duration,
                                        'minutes',
                                      ),
                                    );
                                    setFieldValue(
                                      'salesCutOffTime',
                                      moment(currentStartTime).add(
                                        selectedEventType.duration,
                                        'minutes',
                                      ),
                                    );
                                  }
                                }
                              }}
                              label="Event type"
                              id="eventTypeId"
                              name="eventTypeId"
                              size="small"
                              required
                              renderInput={(
                                params: AutocompleteRenderInputParams,
                              ) => (
                                <MuiTextField
                                  {...params}
                                  name="eventTypeId"
                                  error={
                                    touched['eventTypeId'] &&
                                    !!errors['eventTypeId']
                                  }
                                  helperText={errors['eventTypeId'] as string}
                                  label="Event type"
                                  variant="outlined"
                                />
                              )}
                            />
                          </Box>
                          <FastField
                            component={TextField}
                            label="Name"
                            name="name"
                            size="small"
                          />
                          <Box className="small-formik-select-fix">
                            <FastField
                              component={Select}
                              label="Venue part"
                              id="venuePartId"
                              name="venuePartId"
                              size="small"
                              required
                            >
                              {!!eventOptions?.venueParts &&
                                eventOptions?.venueParts.map((p) => (
                                  <MenuItem key={p.id} value={p.id}>
                                    {p.venue.name + ' - ' + p.name}
                                  </MenuItem>
                                ))}
                            </FastField>
                          </Box>
                          <Box className="small-formik-select-fix">
                            <FastField
                              component={Select}
                              label="Ticket type set"
                              id="ticketTypeSetId"
                              name="ticketTypeSetId"
                              size="small"
                              required
                            >
                              {!!eventOptions?.ticketTypeSets &&
                                eventOptions?.ticketTypeSets.map((s) => (
                                  <MenuItem key={s.id} value={s.id}>
                                    {s.name}
                                  </MenuItem>
                                ))}
                            </FastField>
                          </Box>
                          <Box className="small-date-picker">
                            <FastField
                              component={DateTimePicker}
                              label="Preshow start time"
                              name="preShowStartTime"
                              size="small"
                              onChange={(newValue) => {
                                if (!selected.featureStartTime)
                                  setFieldValue('featureStartTime', newValue);
                              }}
                            />
                          </Box>
                          <Box className="small-date-picker">
                            <Field
                              component={DateTimePicker}
                              label="Feature start time"
                              name="featureStartTime"
                              size="small"
                              onChange={(newValue) => {
                                const selectedEvent =
                                  eventOptions?.eventTypes.find(
                                    (t) => t.id === values.eventTypeId,
                                  );

                                console.log('newValue', newValue);

                                if (selectedEvent) {
                                  setFieldValue(
                                    'featureEndTime',
                                    moment(newValue).add(
                                      selectedEvent.duration,
                                      'minutes',
                                    ),
                                  );
                                  setFieldValue(
                                    'salesCutOffTime',
                                    moment(newValue).add(
                                      selectedEvent.duration,
                                      'minutes',
                                    ),
                                  );
                                }
                              }}
                            />
                          </Box>
                          <Box className="small-date-picker">
                            <FastField
                              component={DateTimePicker}
                              label="Feature end time"
                              name="featureEndTime"
                              size="small"
                            />
                          </Box>
                          <Box className="small-date-picker">
                            <FastField
                              component={DateTimePicker}
                              label="Sales cutoff time"
                              name="salesCutOffTime"
                              size="small"
                            />
                          </Box>
                          <FastField
                            component={CheckboxWithLabel}
                            type="checkbox"
                            name="public"
                            Label={{ label: 'Public' }}
                            size="small"
                          />
                          <Box
                            sx={{
                              textAlign: 'right',
                              margin: '16px 0',
                              display: 'flex',
                              flexDirection: 'row',
                            }}
                          >
                            <Button
                              color="primary"
                              disabled={isSubmitting}
                              onClick={() => setRightDrawerOpen(false)}
                              sx={{ padding: '6px 8px' }}
                            >
                              Cancel
                            </Button>

                            <Box sx={{ flex: '1 1 auto' }} />
                            {!!selected && (
                              <Button
                                color="primary"
                                disabled={isSubmitting}
                                onClick={() => setDialogOpen(true)}
                                sx={{ padding: '6px 8px' }}
                              >
                                Delete
                              </Button>
                            )}
                            <Box sx={{ flex: '1 1 auto' }} />
                            <Button
                              color="primary"
                              variant="contained"
                              disabled={isSubmitting || !isValid}
                              sx={{ padding: '6px 24px' }}
                              onClick={submitForm}
                            >
                              {!!selected ? 'Save' : 'Add'}
                            </Button>
                          </Box>
                        </Box>
                      </Form>
                    )}
                  </Formik>
                </>
              )}
            </Box>
          </Box>
        </Drawer>
      </React.Fragment>
      <Dialog
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{`${alertTitle}`}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`${alertMessage}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Deletion confirmation'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`Are you sure you want to delete this? The event will not be deleted if it has sold tickets.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                const response = await eventDelete(selected.id)();
                setDialogOpen(false);
                if (response) {
                  queryClient.invalidateQueries('eventsGet');
                  navigate('/admin/events/');
                  setDialogOpen(false);
                  setRightDrawerOpen(false);
                }
              } catch (error: any) {
                refetch();
                setDialogOpen(false);
                setRightDrawerOpen(false);
                alert(error.error, error.message);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexGrow: 0,
          alignItems: 'center',
          gap: 1,
          height: '56px',
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontSize: '1.25rem',
          }}
        >
          {event.name}
        </Typography>
        <Button
          color="primary"
          onClick={() => {
            setSelected(event);
            console.log('selected', selected);

            refetchEventOptions();
            setRightDrawerOpen(true);
          }}
        >
          <EditIcon sx={{ ml: -0.5, mr: 0.5 }} />
          Edit
        </Button>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridGap: '16px',
          gridTemplateColumns: {
            xs: 'none',
            sm: 'repeat(auto-fit,minmax(360px,1fr))',
          },
        }}
      >
        <Card
          sx={{
            background: theme.palette.primary.main,
            color: '#ffffff',
          }}
        >
          <CardContent>
            {isWide && (
              <Typography variant="h3" sx={{ fontSize: '1rem', mb: '16px' }}>
                Details
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Typography variant="body1">
                <AccessTimeIcon sx={{ float: 'left', mr: '6px' }} />
                {moment(
                  event.preShowStartTime || event.featureStartTime,
                ).format('L dddd LT')}
              </Typography>
              <Typography variant="body1">
                <LocationOnIcon sx={{ float: 'left', mr: '6px' }} />
                {event.venuePart?.venue.name && (
                  <>
                    {event.venuePart?.venue.name}
                    {' - '}
                  </>
                )}
                {event.venuePart?.name}
              </Typography>

              {event?.name !== event?.eventType?.name && (
                <>
                  <Typography variant="body1">
                    <EventNoteIcon sx={{ float: 'left', mr: '6px' }} />
                    {event.eventType.name}
                  </Typography>
                </>
              )}
              <Typography variant="body1">
                {event?.name === event?.eventType?.name && (
                  <EventNoteIcon sx={{ float: 'left', mr: '6px' }} />
                )}
                {event.eventType?.category && <>{event.eventType.category}</>}
                {event.eventType?.subcategory && (
                  <>
                    {' | '}
                    {event.eventType.subcategory}
                  </>
                )}
                {event.eventType?.duration && (
                  <>
                    {' | '}
                    {formatDuration(event.eventType.duration)}
                  </>
                )}
                {event.eventType?.source && (
                  <>
                    {' | '}
                    {(
                      event.eventType.source.charAt(0).toUpperCase() +
                      event.eventType.source.slice(1).toLowerCase()
                    )
                      .replace('_eu', ' EU')
                      .replace('_us', ' US')}
                  </>
                )}
              </Typography>
              <Typography variant="body1">
                <ConfirmationNumberIcon sx={{ float: 'left', mr: '6px' }} />
                {event.ticketTypeSet.name}
              </Typography>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h3" sx={{ fontSize: '1rem', mb: '16px' }}>
                Tickets
              </Typography>
              <Typography variant="h3" sx={{ fontSize: '1rem', mb: '16px' }}>
                Total inventory:{' '}
                {formatNumber(event.venuePart.numberOfTotalTickets)}
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: 'center',
              }}
            >
              <CircularProgressWithLabel
                label="Sold"
                realvalue={event.paidTicketCount}
                value={
                  (event.paidTicketCount * 100) /
                  (event.venuePart.numberOfTotalTickets -
                    event.venuePart.numberOfHouseTickets)
                }
              />
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h3" sx={{ fontSize: '1rem', mb: '16px' }}>
              Sold tickets
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: { xs: '6px', sm: '16px' },
                justifyContent: 'space-evenly',
              }}
            >
              <CircularProgressWithLabel
                label="Used"
                realvalue={event.usedTicketCount}
                value={
                  !!event.usedTicketCount
                    ? 100 -
                      (event.unusedTicketCount * 100) / event.paidTicketCount
                    : 0
                }
              />
              <CircularProgressWithLabel
                label="Unused"
                realvalue={event.unusedTicketCount}
                value={
                  !!event.unusedTicketCount
                    ? (event.unusedTicketCount * 100) / event.paidTicketCount
                    : 0
                }
              />
            </Box>
          </CardContent>
        </Card>

        {event.tickets.length > 0 && (
          <>
            <Card>
              <CardContent>
                <Typography variant="h3" sx={{ fontSize: '1rem', mb: '16px' }}>
                  Ticket types sold
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}
                >
                  <Box
                    sx={{
                      width: isWide ? '144px' : '120px',
                      height: isWide ? '144px' : '120px',
                      position: 'relative',
                    }}
                  >
                    <Typography
                      sx={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        top: 0,
                        color: theme.palette.text.secondary,
                        pointerEvents: 'none',
                        zIndex: -1,
                      }}
                    >
                      Types
                    </Typography>
                    <Box
                      sx={{
                        zIndex: 100,
                        width: '100%',
                        height: '100%',
                      }}
                    >
                      <Doughnut
                        options={pieOptions as any}
                        data={{
                          labels: ticketTypesSold.map(
                            (ticketType: any) => ticketType.name,
                          ),
                          datasets: [
                            {
                              label: 'Ticket types',
                              data: ticketTypesSold.map(
                                (ticketType: any) => ticketType.count,
                              ),
                              backgroundColor: [
                                theme.palette.primary.main,
                                '#666',
                                '#ccc',
                                '#444',
                                '#999',
                                '#000',
                                '#fff',
                              ],
                              borderWidth: 0,
                            },
                          ],
                        }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Box>
                      {!!ticketTypesSold &&
                        ticketTypesSold.map((ticketType: any, index) => (
                          <Box key={ticketType.id}>
                            <Box
                              sx={{
                                background: pieColors[index],
                                width: '0.75rem',
                                height: '0.75rem',
                                display: 'inline-block',
                                borderRadius: '100px',
                                mr: '6px',
                              }}
                            ></Box>
                            <Typography
                              color="text.secondary"
                              sx={{ display: 'inline' }}
                            >
                              {ticketType.name}: {ticketType.count}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ gridColumn: { xs: 'span 1', md: 'span 2' } }}>
              <CardContent>
                <Typography variant="h3" sx={{ fontSize: '1rem', mb: '16px' }}>
                  Entry distribution
                </Typography>
                <Box sx={{ height: isWide ? '270px' : '160px' }}>
                  <Line
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                          position: 'top' as const,
                        },
                        title: {
                          display: false,
                          text: 'Entry distribution',
                          color: theme.palette.text.secondary,
                          font: { weight: '500', size: 15 },
                        },
                        annotation: {
                          annotations: myAnnotations.map((a: any) => ({
                            scaleID: 'x',
                            type: 'line',
                            value: a,
                            borderColor: theme.palette.text.secondary,
                            borderWidth: 1,
                          })),
                        },
                      },

                      scales: {
                        y: {
                          ticks: {
                            stepSize: 1,
                            autoSkip: true,
                            color: theme.palette.text.secondary,
                          },
                          grid: {
                            color: 'rgba(127 127 127/0.2)',
                          },
                        },
                        x: {
                          type: 'time',
                          ticks: {
                            maxTicksLimit: isWide ? 7 : 6,
                            autoSkip: true,
                            autoSkipPadding: 200,
                            maxRotation: 0,
                            color: theme.palette.text.secondary,
                          },
                          grid: {
                            color: 'rgba(127 127 127/0.2)',
                          },
                          time: {
                            displayFormats: {
                              millisecond: 'HH:mm',
                              second: 'HH:mm',
                            },
                          },
                        },
                      },
                    }}
                    data={data}
                  />
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </>
  );
};
